// Who still has required configuration to fill in, for every surface that says so:
// the "!" badge on a node, the one on an arrow, and the Quick Start banner's steps.
// They all read this one function so they cannot contradict each other — a step
// warning above a node showing a check is exactly the bug this consolidates away.

import { actionNeedsSetup, isFlowAction } from "../../common/configPanel/actionSetup";
import { isHiddenUrlDestinationType } from "../../destinations/isHiddenUrlDestinationType";
import { PROVIDER_TYPES_WITH_BASE_URL } from "../providerBaseUrl";
import { AccountBundle } from "./flowMapTypes";
import { FlowArrow } from "./useFlowGraph";

export type NeedsSetupReport = {
    // Ids of the accounts whose node carries a "!".
    needsSetupAccounts: Set<string>;
    // Ids of the arrows that carry one.
    needsSetupArrows: Set<string>;
    // What the banner counts, and what its "look for the exclamation marks"
    // sentence promises the user will find on the map.
    needsSetupCount: number;
    providerNeedsSetup: boolean;
    destinationsNeedSetup: boolean;
};

export function computeNeedsSetup(input: {
    providerId: string;
    connectionProvider: any;
    bundles: Record<string, AccountBundle>;
    destinations: any[];
    arrows: FlowArrow[];
}): NeedsSetupReport {
    const { providerId, connectionProvider, bundles, destinations, arrows } = input;

    // An account/arrow "needs setup" while any of its actions with required fields
    // has no saved configuration. Optional-only actions don't count — nothing
    // blocks the flow without them.
    const needsConfig = (bundle: AccountBundle | undefined, pred: (a: any) => boolean) =>
        !!bundle && bundle.actions.some((a: any) => pred(a) && actionNeedsSetup(bundle, a));

    const needsSetupAccounts = new Set<string>();
    Object.values(bundles).forEach((b) => {
        // Pull and push belong to the arrows, not to the node.
        if (needsConfig(b, (a: any) => !isFlowAction(a))) needsSetupAccounts.add(b.id);
    });
    // Webhook providers also need their payload parsing configured.
    if (connectionProvider?.type?.webhook && !connectionProvider?.webhook_configuration?.data) {
        needsSetupAccounts.add(providerId);
    }
    // So does a provider whose technology is reached at a URL the user supplies.
    if (PROVIDER_TYPES_WITH_BASE_URL.includes(connectionProvider?.type?.value)
        && !(bundles[providerId]?.base_url || connectionProvider?.base_url)) {
        needsSetupAccounts.add(providerId);
    }
    // And so does a destination with no address, now that the node's own panel asks
    // for one. The technologies that derive their address from configuration are
    // exempt, as they are in the panel.
    Object.values(bundles).forEach((b) => {
        if (!b.isProvider && !b.base_url && !isHiddenUrlDestinationType(b.techName)) {
            needsSetupAccounts.add(b.id);
        }
    });

    const needsSetupArrows = new Set<string>();
    // A flow gap has an owner, and the arrow says who: the account it comes from
    // owes the pull configuration, the one it goes to owes the push. Keeping that
    // apart lets the Quick Start steps name the side with work left, rather than
    // reporting the gap against both of them or against neither.
    const flowGapAccounts = new Set<string>();
    arrows.forEach((arrow) => {
        // Only a direction that is actually carrying data. A direction the user
        // hasn't switched on has nothing missing — its panel already says to
        // configure the sections and then turn it on, so demanding that config up
        // front reports a finished connection as incomplete.
        if (arrow.state !== "enabled") return;
        if (needsConfig(bundles[arrow.fromId], (a: any) => a.type === "pull")) {
            needsSetupArrows.add(arrow.id);
            flowGapAccounts.add(arrow.fromId);
        }
        if (needsConfig(bundles[arrow.toId], (a: any) => a.type === "push")) {
            needsSetupArrows.add(arrow.id);
            flowGapAccounts.add(arrow.toId);
        }
    });

    return {
        needsSetupAccounts,
        needsSetupArrows,
        needsSetupCount: needsSetupAccounts.size + needsSetupArrows.size,
        // "Is this side fully configured?" — which is a wider question than the
        // node badge's "does this node's own panel have work in it", and
        // deliberately so: an account's pull and push configuration is still its
        // own, even though it is edited from the arrow rather than from the node.
        // The two can therefore disagree, and the map stays honest either way,
        // because a flow gap always badges the arrow that carries it.
        // An account whose integration failed to load has no bundle, and "couldn't
        // be inspected" must not be reported as "configured" — the unknown side
        // counts as needing setup, which is what the load-failure banner above the
        // map is already telling the user to retry.
        providerNeedsSetup: !bundles[providerId]
            || needsSetupAccounts.has(providerId)
            || flowGapAccounts.has(providerId),
        // Same question asked of the other side, for the same reason.
        destinationsNeedSetup: destinations.some((d: any) =>
            !bundles[d.id] || needsSetupAccounts.has(d.id) || flowGapAccounts.has(d.id)),
    };
}
